import { useState } from 'react'

import { useAssociateCases } from '../../shared/hooks/useAssociateCases'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HiOutlineCheckCircle } from 'react-icons/hi'

import { Box } from '@chakra-ui/react'

import {
  Box as DarkBox,
  Button as DarkButton,
  MultiSelectInput,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { Button, FormInputSelectControl } from '@/components/ui'
import { OptionI } from '@/utils/forms/optionBuilders'

interface AssociateExisitingCaseFormIProps {
  incidentId?: string
  onClose: () => void
  isRebrand?: boolean
  colorScheme?: ColorScheme
}

export const AssociateExisitingCaseForm = ({
  incidentId,
  onClose,
  isRebrand = false,
  colorScheme = 'dark',
}: AssociateExisitingCaseFormIProps) => {
  const { t } = useTranslation()
  const { caseOptions, isLoading, isAssociationLoading, associateIncident } =
    useAssociateCases({ incidentId, onClose })

  // Dark form controlled state
  const [selectedCases, setSelectedCases] = useState<string[]>([])

  const handleDarkSubmit = () => {
    const cases = selectedCases.map((id) => {
      const opt = caseOptions.find((o: { value: string }) => o.value === id)
      return { value: id, label: opt?.label ?? id }
    })
    associateIncident({ cases })
  }

  // Legacy form (react-hook-form)
  const { handleSubmit, control, watch } = useForm<{
    cases: OptionI[]
  }>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
  })
  const areCasesSelected = watch('cases')?.length > 0

  if (isRebrand) {
    return (
      <DarkBox>
        <MultiSelectInput
          colorScheme={colorScheme}
          intentLabel={t('incidents:associate_case_search_cases')}
          maxResults={7}
          noMatchLabel={t('incidents:associate_case_no_results')}
          onClear={() => setSelectedCases([])}
          onRemove={(value) =>
            setSelectedCases((prev) => prev.filter((v) => v !== value))
          }
          onToggleOption={(value) => {
            setSelectedCases((prev) =>
              prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value]
            )
          }}
          options={caseOptions}
          placeholder={t('incidents:cases_placeholder')}
          resultsHeader={t('incidents:associate_case_select_cases')}
          searchPlaceholder={t('incidents:associate_case_search_cases')}
          searchable
          selected={selectedCases}
        />

        <DarkBox display='flex' gap='8px' justifyContent='flex-end' mt='16px'>
          <DarkButton
            colorScheme={colorScheme}
            onClick={onClose}
            variant='secondary'
          >
            {t('common:cancel')}
          </DarkButton>
          <DarkButton
            colorScheme={colorScheme}
            disabled={selectedCases.length === 0}
            isLoading={isAssociationLoading}
            onClick={handleDarkSubmit}
          >
            {t('cases:associate')}
          </DarkButton>
        </DarkBox>
      </DarkBox>
    )
  }

  return (
    <Box>
      <Box mb={3} py={2}>
        <FormInputSelectControl
          closeMenuOnSelect={false}
          control={control}
          defaultValue={null}
          id='cases'
          isClearable
          isDisabled={isLoading}
          isMulti
          isSearchable
          label='Cases'
          options={caseOptions}
          placeholder='Add cases to associate incident with'
          rules={{ required: true }}
        />
      </Box>

      <Box display='flex' justifyContent='flex-end'>
        <Button mr={3} onClick={onClose} variant='secondary'>
          Cancel
        </Button>
        <Button
          isDisabled={!areCasesSelected}
          isLoading={isAssociationLoading}
          leftIcon={<HiOutlineCheckCircle size='24px' />}
          loadingText='Associating'
          onClick={handleSubmit(associateIncident)}
        >
          Associate
        </Button>
      </Box>
    </Box>
  )
}
